/**
 * Drop-file → resolve queue client util.
 *
 * 1. Classify the file into one of image|video|audio|file.
 * 2. For text-readable types, read up to TEXT_EXTRACT_LIMIT chars locally.
 * 3. Upload the binary directly to the 'shared-media' bucket under
 *    `${userId}/uploads/...`. Direct upload is required because the 300 MB
 *    bucket limit blows past Netlify's ~6 MB synchronous body cap.
 * 4. POST metadata to the ingest-file lambda, which materializes the
 *    shared_content + things_to_review + media + media_parts rows.
 */

import { callLambda } from './lambdaApi';
import { supabase } from './supabaseClient';
import { classifyFile, slugifyFilename, extension, type PartKind } from '@eb/graph';

const BUCKET = 'shared-media';
const TEXT_EXTRACT_LIMIT = 256 * 1024; // 256 KB

const TEXT_EXTENSIONS = new Set([
  'md', 'markdown', 'mdx',
  'txt', 'log', 'csv', 'tsv',
  'json', 'yaml', 'yml', 'toml', 'xml', 'html', 'htm',
  'js', 'jsx', 'ts', 'tsx', 'mjs', 'cjs',
  'py', 'rb', 'go', 'rs', 'java', 'kt', 'swift',
  'c', 'h', 'cpp', 'hpp', 'cc', 'cs',
  'php', 'sh', 'bash', 'zsh', 'fish',
  'sql', 'graphql', 'gql',
  'css', 'scss', 'sass', 'less',
  'env', 'gitignore', 'editorconfig',
]);

// File classification lives in @eb/graph (shared with uploadMedia). Re-export
// the kind type under the historical name for this module's callers.
export type IngestKind = PartKind;
export { classifyFile };

/**
 * Fired on window after the ingest-file lambda has written every row
 * (shared_content + things_to_review + media + media_parts + backlink).
 * The uploading session can't rely on realtime to echo its own write, so
 * useThingsToReview listens for this to refetch deterministically.
 */
export const THINGS_TO_REVIEW_CHANGED_EVENT = 'eb:things-to-review-changed';

export interface IngestResult {
  thing_to_review_id: string;
  media_id: string;
  knowledge_node_id: string;
  shared_content_id: string;
}

function isTextReadable(mime: string, filename: string): boolean {
  if (mime.startsWith('text/')) return true;
  if (mime === 'application/json') return true;
  if (mime === 'application/xml') return true;
  return TEXT_EXTENSIONS.has(extension(filename));
}

async function extractText(file: File): Promise<string | undefined> {
  try {
    const slice = file.slice(0, TEXT_EXTRACT_LIMIT);
    const text = await slice.text();
    return text.length > 0 ? text : undefined;
  } catch {
    return undefined;
  }
}

export interface IngestOptions {
  onPhase?: (phase: 'uploading' | 'ingesting') => void;
  contentKind?: 'captured_media' | 'video_log';
  title?: string;
  recordedAt?: string;
  durationMs?: number;
  liveTranscript?: {
    text: string;
    language: string | null;
    words: Array<{
      text: string;
      start: number;
      end: number;
      confidence: number | null;
    }>;
  };
}

export async function ingestFile(
  file: File,
  options: IngestOptions = {},
): Promise<IngestResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData.session?.user?.id;
  if (!userId) throw new Error('Not authenticated');

  const { mime, kind } = classifyFile(file);

  const extracted = isTextReadable(mime, file.name) ? await extractText(file) : undefined;

  const storagePath = `${userId}/uploads/${Date.now()}-${slugifyFilename(file.name)}`;

  options.onPhase?.('uploading');
  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(storagePath, file, {
    contentType: mime,
    upsert: false,
  });
  if (uploadErr) throw new Error(`Upload failed: ${uploadErr.message}`);

  const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);

  options.onPhase?.('ingesting');
  const result = await callLambda<IngestResult>('ingestFile', {
    method: 'POST',
    body: JSON.stringify({
      storage_path: storagePath,
      public_url: pub.publicUrl,
      filename: file.name,
      size_bytes: file.size,
      mime_type: mime,
      kind,
      extracted_text: extracted,
      content_kind: options.contentKind ?? 'captured_media',
      title: options.title,
      recorded_at: options.recordedAt,
      duration_ms: options.durationMs,
      live_transcript: options.liveTranscript,
    }),
  });

  window.dispatchEvent(new CustomEvent(THINGS_TO_REVIEW_CHANGED_EVENT));

  return result;
}
